// First import on purpose: the module promotes the brand kit preload
// (index.html) to a stylesheet when it evaluates, which — as the first import —
// is before anything else in the bundle evaluates (011-27 FR-010).
import "@/shared/fonts/promote-brand-kit";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AppProviders } from "@/app/providers/app-providers";
import { AppRouter } from "@/app/router/app-router";
import { AdminAccessUnavailablePage } from "@/app/router/realm-boundary";
import {
  getRuntimeConfigEnvironment,
  loadRuntimeConfig,
  shouldAllowInlineRuntimeFallback,
} from "@/app/config/runtime-config";
import { getBrowserOrigin, resolveRealmContext } from "@/core/auth/realm";
import "@/shared/styles/index.css";

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("CLM Web root element was not found.");
}

const root = createRoot(rootElement);

void loadRuntimeConfig({
  allowInlineFallback: shouldAllowInlineRuntimeFallback(getRuntimeConfigEnvironment()),
})
  .then(({ config }) => {
    const realmContext = resolveRealmContext(config, getBrowserOrigin());

    if (realmContext.realm === "admin-unavailable") {
      // Fail closed: admin-style host without a configured admin realm renders
      // a static page with no auth provider and no auth calls.
      root.render(
        <StrictMode>
          <AdminAccessUnavailablePage />
        </StrictMode>,
      );
      return;
    }

    root.render(
      <StrictMode>
        <AppProviders initialRuntimeConfig={config}>
          <AppRouter realm={realmContext.realm} />
        </AppProviders>
      </StrictMode>,
    );
  })
  .catch((error: unknown) => {
    console.error("Unable to bootstrap CLM Web runtime configuration.", error);

    root.render(
      <StrictMode>
        <main className="clm-page-gutter clm-section-spacing min-h-screen bg-background text-foreground">
          <div className="mx-auto max-w-3xl rounded-lg border bg-card p-8 shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-wide text-clm-brand-blue-dark">
              Runtime configuration error
            </p>
            <h1 className="mt-3 text-3xl font-bold text-clm-primary-blue">
              CLM Web could not start
            </h1>
            <p className="mt-4 text-muted-foreground">
              Runtime configuration is unavailable for this environment. Please retry or contact
              support if the issue persists.
            </p>
          </div>
        </main>
      </StrictMode>,
    );
  });
